import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, Loader2, Clock, ShieldCheck, ArrowRight, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

const STATUS_STYLES = {
  "Waiting for Payment": "bg-slate-500/15 text-slate-300 border-slate-500/30",
  "Transaction Detected": "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "Confirming": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Paid": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Expired": "bg-red-500/15 text-red-300 border-red-500/30",
  "Failed": "bg-red-500/15 text-red-300 border-red-500/30",
};

function Countdown({ expiresAt }) {
  const { t } = useI18n();
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.floor((new Date(expiresAt) - new Date()) / 1000)));
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, [expiresAt]);
  if (left <= 0) return <span className="text-red-400 font-mono">{t("pay.expired")}</span>;
  const m = String(Math.floor(left / 60)).padStart(2, "0");
  const s = String(left % 60).padStart(2, "0");
  return <span className="font-mono text-amber-400 text-lg" data-testid="payment-countdown">{m}:{s}</span>;
}

export default function Payment() {
  const { orderId } = useParams();
  const { t } = useI18n();
  const [payment, setPayment] = useState(null);
  const [order, setOrder] = useState(null);
  const [txHash, setTxHash] = useState("");
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nf, setNf] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/orders/${orderId}`); setOrder(data.order); setPayment(data.payment); }
    catch { setNf(true); }
  }, [orderId]);

  useEffect(() => { load(); const id = setInterval(load, 12000); return () => clearInterval(id); }, [load]);

  useEffect(() => {
    if (payment?.status === "Paid" && order && !sessionStorage.getItem(`adx_purchase_${orderId}`)) {
      track("Purchase", { transaction_id: orderId, value: payment.amount, currency: "USDT" });
      sessionStorage.setItem(`adx_purchase_${orderId}`, "1");
    }
  }, [payment, order, orderId]);

  const copy = () => { navigator.clipboard.writeText(payment.wallet_address); setCopied(true); toast.success(t("pay.copied")); setTimeout(() => setCopied(false), 2000); };

  const submitTx = async () => {
    if (!txHash.trim()) { toast.error("Enter a transaction hash"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/payments/${orderId}/submit-tx`, { tx_hash: txHash.trim() });
      toast.success(data.message);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setSubmitting(false);
  };

  if (nf) return <div className="section-lux container-lux text-center"><h1 className="text-2xl font-bold text-white">Order not found</h1><Link to="/dashboard" className="gold-btn mt-6 inline-flex">Go to Dashboard</Link></div>;
  if (!payment) return <div className="flex justify-center py-40"><Loader2 className="animate-spin text-amber-400" size={40} /></div>;

  const paid = payment.status === "Paid";

  return (
    <div className="section-lux">
      <div className="container-lux max-w-3xl">
        <div className="text-center mb-8">
          <div className="label-lux mb-2">{t("pay.orderId")}: {orderId}</div>
          <h1 className="text-3xl font-extrabold text-white">{t("pay.title")}</h1>
        </div>

        {paid ? (
          <div className="card-lux p-10 text-center" data-testid="payment-success">
            <div className="h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
              <Check className="text-emerald-400" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-white">Payment Confirmed</h2>
            <p className="text-slate-400 mt-3">Your payment has been verified. We're starting your service.</p>
            <Link to="/dashboard/orders" className="gold-btn mt-6 inline-flex" data-testid="payment-view-orders">View My Orders <ArrowRight size={16} /></Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card-lux p-6 flex flex-col items-center justify-center text-center">
              <p className="label-lux mb-3">{t("pay.scan")}</p>
              <div className="bg-white p-4 rounded-xl" data-testid="payment-qr">
                <QRCodeSVG value={payment.wallet_address} size={168} level="M" />
              </div>
              <div className="mt-4 text-xs text-slate-500">USDT · {payment.network}</div>
            </div>

            <div className="card-lux p-6 space-y-4">
              <div>
                <div className="label-lux">{t("pay.amount")}</div>
                <div className="font-display text-3xl font-extrabold gold-text mt-1" data-testid="payment-amount">{payment.amount} USDT</div>
              </div>
              <div>
                <div className="label-lux">{t("pay.network")}</div>
                <div className="text-white font-medium mt-1">USDT {payment.network}</div>
              </div>
              <div>
                <div className="label-lux">{t("pay.walletAddr")}</div>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 text-xs text-slate-300 bg-black/40 rounded-lg px-3 py-2.5 break-all font-mono" data-testid="payment-wallet">{payment.wallet_address}</code>
                  <button onClick={copy} className="silver-btn !p-2.5 shrink-0" data-testid="payment-copy-wallet">{copied ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}</button>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-black/30 px-4 py-3">
                <span className="flex items-center gap-2 text-sm text-slate-400"><Clock size={15} /> {t("pay.expires")}</span>
                <Countdown expiresAt={payment.expires_at} />
              </div>
            </div>

            <div className="md:col-span-2 card-lux p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="label-lux">{t("pay.status")}</div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${STATUS_STYLES[payment.status] || STATUS_STYLES["Waiting for Payment"]}`} data-testid="payment-status-badge">{payment.status}</span>
              </div>
              <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-4 flex gap-3 mb-4">
                <ShieldCheck className="text-amber-400 shrink-0" size={18} />
                <p className="text-xs text-slate-400 leading-relaxed">{t("pay.note")} Submitting a transaction hash never marks a payment as paid on its own — each transaction is reviewed and verified before your order is confirmed.</p>
              </div>
              <label className="label-lux">{t("pay.txHash")}</label>
              <div className="flex flex-col sm:flex-row gap-2 mt-2">
                <input className="input-lux font-mono text-xs" placeholder={t("pay.txPlaceholder")} value={txHash} onChange={(e) => setTxHash(e.target.value)} data-testid="payment-txhash-input" />
                <button onClick={submitTx} disabled={submitting} className="gold-btn whitespace-nowrap" data-testid="payment-submit-tx-btn">
                  {submitting ? <Loader2 className="animate-spin" size={16} /> : t("pay.submitTx")}
                </button>
              </div>
              {payment.status === "Transaction Detected" && (
                <div className="mt-4 flex gap-2 text-xs text-blue-300"><AlertTriangle size={14} /> Transaction received. Verification is in progress or pending manual review by our team.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
